import { QueryClient } from "@tanstack/react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: async ({ queryKey }) => {
        // Use current window location for API requests (works for both localhost:5000 and Replit)
        const currentPort = window.location.port;
        const currentHost = window.location.hostname;
        const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
        
        // For localhost, use same port as current page; for Replit, use empty baseUrl
        const baseUrl = isLocalhost ? `http://${currentHost}:${currentPort}` : '';
        const url = queryKey[0] as string;
        const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
        
        const res = await fetch(fullUrl, {
          credentials: 'include', // Include cookies for authentication
          cache: 'no-cache',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (!res.ok) {
          throw new Error(`${res.status}: ${res.statusText}`);
        }
        return res.json();
      },
    },
  },
});

interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: any;
  data?: any;
}

export const apiRequest = async (methodOrUrl: string, urlOrOptions?: string | ApiRequestOptions, body?: any) => {
  let method: string;
  let url: string;
  let options: ApiRequestOptions = {};

  // Handle both function signatures
  if (typeof urlOrOptions === 'string') {
    // Called as apiRequest(method, url, body)
    method = methodOrUrl;
    url = urlOrOptions;
    if (body !== undefined) {
      options.body = body;
    }
  } else {
    // Called as apiRequest(url, options)
    method = 'GET';
    url = methodOrUrl;
    options = urlOrOptions || {};
    if (options.method) {
      method = options.method;
    }
    if (options.data !== undefined && options.body === undefined) {
      options.body = options.data;
    }
  }
  // Use current window location for API requests (works for both localhost:5000 and Replit)
  const currentPort = window.location.port;
  const currentHost = window.location.hostname;
  const isLocalhost = currentHost === 'localhost' || currentHost === '127.0.0.1';
  
  // For localhost, use same port as current page; for Replit, use empty baseUrl
  const baseUrl = isLocalhost ? `http://${currentHost}:${currentPort}` : '';
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${url}`;
  
  const res = await fetch(fullUrl, {
    method: method,
    headers: {
      "Content-Type": "application/json",
      "Cache-Control": "no-cache",
      "Pragma": "no-cache",
      ...options.headers,
    },
    credentials: 'include', // Include cookies for authentication
    cache: 'no-cache', // Prevent caching issues
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) {
    let errorData: any = {};
    
    // Try to parse response as JSON to get error details including plan limits
    try {
      errorData = await res.json();
      console.log('API Error Response Data:', errorData);
    } catch (jsonError) {
      console.error('Error parsing error response as JSON:', jsonError);
      
      // Check if we're getting HTML instead of JSON (common in CORS/routing issues)
      const contentType = res.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        console.error('Received HTML response instead of JSON - likely a routing or CORS issue');
        console.error('Check if API endpoint exists and server is properly configured for localhost:3001');
      }
    }
    
    // Create error with message and preserve all data
    let errorMessage: string;
    if (errorData?.error) {
      errorMessage = errorData.error;
    } else if (errorData?.message) {
      errorMessage = errorData.message;
    } else if (res.status === 401) {
      errorMessage = 'Authentication failed. Please log in again.';
    } else if (res.status === 403) {
      errorMessage = 'You do not have permission to perform this action.';
    } else if (res.status === 404) {
      errorMessage = 'The requested resource was not found.';
    } else if (res.status >= 500) {
      errorMessage = 'A server error occurred. Please try again later.';
    } else {
      errorMessage = `Request failed with status: ${res.status}`;
    }
    
    const error = new Error(errorMessage) as any;
    error.data = errorData; // Always preserve the full error response for plan limits
    throw error;
  }

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return null;
  }
  return res.json();
};

export { queryClient };